import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";
import { ChevronRight, Calculator, Check } from "lucide-react";
import QuoteWizard from "../components/QuoteWizard";

export default function RoomTypeDetail() {
  const { slug } = useParams();
  const [room, setRoom] = useState(null);
  const [roomCount, setRoomCount] = useState(10);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    api.get(`/room-types/${slug}`).then((r) => setRoom(r.data)).catch(() => {});
  }, [slug]);

  useSeo({
    title: room ? room.seo_title || `${room.name_tr} Ekipman Listesi — Otel Pro` : "Otel Pro",
    description: room?.seo_description || room?.description,
  });

  if (!room) return <Layout><div className="section-pad container-site">Yükleniyor...</div></Layout>;

  const defaultChecklist = [
    { name: "Banyo Havlusu (500gsm)", perRoom: 4, required: true, star: "4+ yıldız" },
    { name: "Yüz Havlusu (400gsm)", perRoom: 4, required: true, star: "3+ yıldız" },
    { name: "Saten Nevresim Takımı", perRoom: 2, required: true, star: "4+ yıldız" },
    { name: "Premium Şampuan 30ml (aylık)", perRoom: 30, required: true, star: "tüm seviyeler" },
    { name: "Duş Jeli 30ml (aylık)", perRoom: 30, required: true, star: "tüm seviyeler" },
    { name: "Oda Minibarı 40L", perRoom: 1, required: true, star: "3+ yıldız" },
    { name: "Elektrikli Kettle", perRoom: 1, required: false, star: "4+ yıldız" },
    { name: "Dijital Oda Kasası", perRoom: 1, required: true, star: "4+ yıldız" },
  ];
  const checklist = room.checklist?.length ? room.checklist : defaultChecklist.map((c) => ({
    product_name: c.name, quantity: c.perRoom, required: c.required, star_note: c.star,
  }));

  const withTotals = checklist.map((c) => ({
    ...c,
    total: (c.quantity || c.perRoom || 1) * (roomCount || 1),
  }));

  const items = withTotals.map((c) => ({
    name: c.product_name || c.name,
    quantity: c.total,
    notes: c.star_note || "",
  }));

  return (
    <Layout>
      <div className="bg-softbg">
        <div className="container-site py-5 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <Link to="/" className="hover:text-navy">Ana Sayfa</Link>
          <ChevronRight size={12} />
          <Link to="/oda-tipleri" className="hover:text-navy">Oda Planlayıcı</Link>
          <ChevronRight size={12} />
          <span className="text-navy font-medium">{room.name_tr}</span>
        </div>
      </div>

      <section
        className="relative bg-navy text-white py-20"
        style={room.image ? { backgroundImage: `linear-gradient(rgba(8,17,29,0.85), rgba(8,17,29,0.7)), url(${room.image})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
      >
        <div className="container-site max-w-3xl">
          <p className="accent-script text-gold text-2xl mb-2">Oda Tipi</p>
          <h1 className="heading-serif text-3xl md:text-5xl font-semibold">{room.name_tr}</h1>
          <p className="text-white/75 mt-4">{room.description}</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-site max-w-4xl">
          <div className="bg-softbg rounded-xl p-6 mb-6 flex flex-wrap items-center gap-5" data-testid="room-calc-input">
            <Calculator className="text-gold" size={22} />
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Oda Sayısı</div>
              <div className="text-sm text-navy font-medium">Toplam adet otomatik hesaplanır</div>
            </div>
            <input
              type="number"
              value={roomCount}
              min={1}
              onChange={(e) => setRoomCount(Number(e.target.value) || 1)}
              className="w-24 ml-auto rounded-md border border-gray-200 px-3 py-2 bg-white"
              data-testid="room-count-input"
            />
          </div>

          <div className="rounded-xl overflow-hidden border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-navy text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Ekipman</th>
                  <th className="text-center px-4 py-3 font-medium">Oda Başı</th>
                  <th className="text-center px-4 py-3 font-medium">Toplam</th>
                  <th className="text-center px-4 py-3 font-medium">Zorunluluk</th>
                </tr>
              </thead>
              <tbody>
                {withTotals.map((c, i) => (
                  <tr key={i} className={i % 2 ? "bg-softbg" : "bg-white"} data-testid={`checklist-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-navy">{c.product_name || c.name}</div>
                      {c.star_note && <div className="text-[11px] text-gray-500 mt-0.5">{c.star_note}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">{c.quantity || c.perRoom || 1}</td>
                    <td className="px-4 py-3 text-center font-semibold text-navy">{c.total}</td>
                    <td className="px-4 py-3 text-center">
                      {c.required ? (
                        <span className="inline-flex items-center gap-1 text-rose-600 text-xs"><Check size={12} /> Zorunlu</span>
                      ) : (
                        <span className="text-gray-400 text-xs">Opsiyonel</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setWizardOpen(true)} className="btn-gold" data-testid="room-detail-send-quote">
              Bu Listeyi Teklif Olarak Gönder
            </button>
            <a
              href={`${api.defaults.baseURL}/room-types/${slug}/pdf?room_count=${roomCount}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold"
              data-testid="room-detail-download-pdf"
            >
              Checklist PDF İndir
            </a>
            <Link to="/hesaplama" className="btn-outline-gold" data-testid="room-detail-calc">
              Hesaplama Aracına Git
            </Link>
          </div>
        </div>
      </section>

      <QuoteWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialSource="room_checklist"
        initialItems={items}
        initialNotes={`${room.name_tr} — ${roomCount} oda için ekipman listesi talebi.`}
      />
    </Layout>
  );
}
